import { greedyBot } from '@horde/bots';
import { createGame, legalActions, reduce, replay, viewFor } from '@horde/engine';
import type {
  Action,
  CardId,
  GameConfig,
  GameEvent,
  GameState,
  PlayerId,
  RngState,
  TerritoryId,
} from '@horde/engine';
import { worldMap } from '@horde/map-world';
import { create } from 'zustand';
import { describeEvent, playerName, type Tone } from './lib/format';
import {
  beatsForMove,
  beatsForSteps,
  fallCause,
  moveHold,
  type Banner,
  type Beat,
  type Floater,
  type Pace,
  type Step,
} from './lib/playback';
import { hapticsFor, setKeepAwake } from './native';
import type { GameRow, SeatRow } from './online/client';

/** The Supabase client loads only when online play is first used. */
const loadNet = () => import('./online/client');
import { play, sfxFor } from './sound';
import { recordOutcome } from './stats';
import { clearSave, loadSave, writeSave } from './persist';

export type Screen = 'title' | 'new' | 'board' | 'online' | 'lobby';

export interface LogLine {
  id: number;
  round: number;
  text: string;
  tone: Tone;
}

export interface Selection {
  from: TerritoryId | null;
  to: TerritoryId | null;
  card: CardId | null;
  dice: 1 | 2 | 3;
}

export interface Reveal {
  cardId: CardId;
  playerId: PlayerId;
  effects: string[];
}

interface Store {
  screen: Screen;
  game: GameState | null;
  config: GameConfig | null;
  log: Action[];
  botRng: RngState;
  /** Increments on every accepted action; lets the map re-trigger animations. */
  tick: number;
  lastEvents: GameEvent[];
  lines: LogLine[];
  selection: Selection;
  error: string | null;
  hasSave: boolean;
  reveal: Reveal | null;
  fast: boolean;
  /** Pass-and-play: the human who must confirm they hold the device before their turn shows. */
  handoffFor: PlayerId | null;
  /** Online game context; null for local play. */
  online: OnlineCtx | null;
  /** True while an online action is in flight. */
  sending: boolean;
  /** An online attack waiting on its dice: the board shows them tumbling. */
  rolling: Rolling | null;
  /** A batch of moves is being played back; input waits until it ends. */
  replaying: boolean;
  /** What is happening right now, in words, while someone else plays. */
  caption: string | null;
  /** A title across the board (a rising, a player falling). */
  banner: (Banner & { key: number }) | null;
  /** Labels floating over territories ("+2 zombies", "Overrun!"). */
  floaters: (Floater & { key: number; at: number })[];
  /** A human player was knocked out: shown as a card until dismissed. */
  fallen: Fallen | null;
  /** One line on the last rising, kept for the next player to read. */
  lastRising: string | null;

  setScreen(screen: Screen): void;
  startGame(config: GameConfig): void;
  resume(): boolean;
  abandon(): void;
  dispatch(action: Action): boolean;
  select(patch: Partial<Selection>): void;
  clearSelection(): void;
  dismissReveal(): void;
  botStep(): void;
  toggleFast(): void;
  confirmHandoff(): void;
  skipPlayback(): void;
  dismissFallen(): void;

  openLobby(gameId: string): Promise<void>;
  enterOnlineGame(gameId: string): Promise<void>;
  leaveOnline(): void;
  requestTimeout(): Promise<void>;
}

export interface Fallen {
  playerId: PlayerId;
  name: string;
  cause: string;
  round: number;
}

export interface Rolling {
  from: TerritoryId;
  to: TerritoryId;
  attackDice: number;
  defendDice: number;
}

export interface OnlineCtx {
  gameId: string;
  code: string;
  /** My seat's player id, or null when spectating. */
  myPlayerId: PlayerId | null;
  hostId: string;
  userId: string;
  status: GameRow['status'];
  seats: SeatRow[];
  lastActionAt: string;
  /** Number of actions applied so far (next expected seq). */
  appliedSeq: number;
  unsubscribe: (() => void) | null;
}

export function humanCount(state: GameState): number {
  return state.players.filter((p) => p.kind === 'human' && !p.eliminated).length;
}

/** Whether the device needs to change hands before this player's turn is shown. */
function needsHandoff(state: GameState): PlayerId | null {
  if (state.outcome) return null;
  const current = state.players[state.currentPlayerIndex]!;
  if (current.kind !== 'human') return null;
  return humanCount(state) >= 2 ? current.id : null;
}

const emptySelection: Selection = { from: null, to: null, card: null, dice: 3 };
let lineId = 0;

function linesFor(events: GameEvent[], state: GameState): LogLine[] {
  const out: LogLine[] = [];
  for (const e of events) {
    const d = describeEvent(e, state);
    if (d) out.push({ id: ++lineId, round: state.round, text: d.text, tone: d.tone });
  }
  return out;
}

/** The human player whose hand and controls are shown. */
export function viewerId(state: GameState): PlayerId {
  const current = state.players[state.currentPlayerIndex]!;
  if (current.kind === 'human') return current.id;
  return state.players.find((p) => p.kind === 'human')?.id ?? current.id;
}

export const useStore = create<Store>((set, get) => ({
  screen: 'title',
  game: null,
  config: null,
  log: [],
  botRng: { seed: 0, calls: 0 },
  tick: 0,
  lastEvents: [],
  lines: [],
  selection: emptySelection,
  error: null,
  hasSave: loadSave() !== null,
  reveal: null,
  fast: false,
  handoffFor: null,
  online: null,
  sending: false,
  rolling: null,
  replaying: false,
  caption: null,
  banner: null,
  floaters: [],
  fallen: null,
  lastRising: null,

  setScreen: (screen) => {
    if (screen !== 'board') void setKeepAwake(false);
    set({ screen });
  },

  startGame: (config) => {
    resetPlayback();
    const game = createGame(worldMap, config);
    const botRng = { seed: (config.seed ^ 0x5eed) >>> 0, calls: 0 };
    writeSave({ config, log: [], botRng, savedAt: Date.now() });
    void setKeepAwake(true);
    set({
      screen: 'board',
      game,
      config,
      log: [],
      botRng,
      tick: 0,
      lastEvents: [],
      lines: [{ id: ++lineId, round: 1, text: 'The outbreak begins.', tone: 'horde' }],
      selection: emptySelection,
      error: null,
      hasSave: true,
      reveal: null,
      handoffFor: needsHandoff(game),
    });
  },

  resume: () => {
    resetPlayback();
    const file = loadSave();
    if (!file) return false;
    try {
      const game = replay(worldMap, file.config, file.log);
      void setKeepAwake(true);
      set({
        screen: 'board',
        game,
        config: file.config,
        log: file.log,
        botRng: file.botRng,
        tick: 0,
        lastEvents: [],
        lines: [{ id: ++lineId, round: game.round, text: 'Game resumed.', tone: 'neutral' }],
        selection: emptySelection,
        error: null,
        reveal: null,
        handoffFor: needsHandoff(game),
      });
      return true;
    } catch {
      clearSave();
      set({ hasSave: false });
      return false;
    }
  },

  abandon: () => {
    resetPlayback();
    clearSave();
    void setKeepAwake(false);
    set({ screen: 'title', game: null, config: null, log: [], hasSave: false, reveal: null });
  },

  dispatch: (action) => {
    const { game, config, log, botRng, online } = get();
    if (!game || !config) return false;
    // The board is mid-playback: it is not the real board yet.
    if (get().replaying) return false;
    if (online) return queueOnline(action);
    const r = reduce(worldMap, game, action);
    if (!r.ok) {
      set({ error: r.error });
      return false;
    }
    const nextLog = [...log, action];
    writeSave({ config, log: nextLog, botRng, savedAt: Date.now() });

    const turnChanged = r.state.currentPlayerIndex !== game.currentPlayerIndex;
    const beats: StoreBeat[] = beatsForMove(game, r.state, r.events, action, paceNow());
    // The first beat answers the tap; the last lands the turn change.
    beats[0] = {
      ...beats[0]!,
      extra: { selection: selectionAfter(get().selection, action, game, r.state) },
    };
    const last = beats.length - 1;
    beats[last] = {
      ...beats[last]!,
      extra: {
        ...beats[last]!.extra,
        handoffFor: turnChanged ? needsHandoff(r.state) : get().handoffFor,
      },
    };
    set({ log: nextLog, error: null });
    playBeats(beats);
    if (r.state.outcome) clearSave();
    return true;
  },

  select: (patch) => set({ selection: { ...get().selection, ...patch }, error: null }),
  clearSelection: () =>
    set({ selection: { ...emptySelection, dice: get().selection.dice }, error: null }),
  dismissReveal: () => set({ reveal: null }),

  botStep: () => {
    const { game, botRng } = get();
    if (!game || game.outcome) return;
    const me = game.players[game.currentPlayerIndex]!;
    if (me.kind !== 'bot') return;
    const legal = legalActions(worldMap, game, me.id);
    if (legal.length === 0) return;
    const [action, nextRng] = greedyBot.choose(worldMap, viewFor(game, me.id), legal, botRng);
    set({ botRng: nextRng });
    get().dispatch(action);
  },

  toggleFast: () => set({ fast: !get().fast }),
  confirmHandoff: () => set({ handoffFor: null }),
  skipPlayback: () => skipBeats(),
  dismissFallen: () => set({ fallen: null }),

  openLobby: async (gameId) => {
    const net = await loadNet();
    get().leaveOnline();
    const userId = await net.ensureSignedIn();
    const { game, seats } = await net.fetchGame(gameId);
    const ctx = makeCtx(game, seats, userId);
    ctx.unsubscribe = net.subscribeGame(gameId, {
      onSeats: () => void refreshSeats(),
      onGame: (row) => {
        if (row.status === 'active') void get().enterOnlineGame(gameId);
        else {
          const o = get().online;
          if (o) set({ online: { ...o, status: row.status } });
        }
      },
    });
    set({ online: ctx, screen: 'lobby', error: null });
    if (game.status !== 'lobby') void get().enterOnlineGame(gameId);
  },

  enterOnlineGame: async (gameId) => {
    resetQueue();
    resetPlayback();
    const net = await loadNet();
    const prev = get().online;
    if (prev?.unsubscribe) prev.unsubscribe();
    const userId = await net.ensureSignedIn();
    const { game, seats } = await net.fetchGame(gameId);
    if (!seats.some((s) => s.user_id === userId)) {
      set({ error: 'You do not have a seat in this game' });
      return;
    }
    // The server writes each seat's view as the game starts; give it a moment.
    let view = await net.fetchMyView(gameId);
    for (let i = 0; !view && i < 5; i++) {
      await new Promise((r) => setTimeout(r, 400));
      view = await net.fetchMyView(gameId);
    }
    if (!view) {
      set({ error: 'The game has not started yet' });
      return;
    }
    const state = view.state;
    const ctx = makeCtx(game, seats, userId);
    ctx.appliedSeq = view.seq;
    ctx.unsubscribe = net.subscribeGame(gameId, {
      onView: (row) => receiveView(row.seq, row.state, row.events, row.steps ?? []),
      onGame: (row) => {
        const o = get().online;
        if (o) set({ online: { ...o, status: row.status, lastActionAt: row.last_action_at } });
      },
    });
    void setKeepAwake(true);
    set({
      screen: 'board',
      game: state,
      config: state.config,
      log: [],
      online: ctx,
      tick: 0,
      lastEvents: [],
      lines: [
        { id: ++lineId, round: state.round, text: `Joined game ${game.code}.`, tone: 'neutral' },
      ],
      selection: emptySelection,
      error: null,
      reveal: null,
      handoffFor: null,
    });
  },

  leaveOnline: () => {
    resetQueue();
    resetPlayback();
    const o = get().online;
    if (o?.unsubscribe) o.unsubscribe();
    void setKeepAwake(false);
    set({
      online: null,
      game: null,
      config: null,
      log: [],
      screen: 'online',
      sending: false,
      error: null,
    });
  },

  requestTimeout: async () => {
    const o = get().online;
    if (!o) return;
    set({ sending: true, error: null });
    try {
      const net = await loadNet();
      const res = await net.submitTimeout(o.gameId);
      receiveView(res.seq, res.state, res.events, res.steps ?? []);
    } catch (e) {
      set({ error: (e as Error).message });
    } finally {
      set({ sending: false });
    }
  },
}));

function makeCtx(game: GameRow, seats: SeatRow[], userId: string): OnlineCtx {
  const mine = seats.find((s) => s.user_id === userId);
  return {
    gameId: game.id,
    code: game.code,
    myPlayerId: mine ? `p${mine.seat + 1}` : null,
    hostId: game.host_id,
    userId,
    status: game.status,
    seats,
    lastActionAt: game.last_action_at,
    appliedSeq: 0,
    unsubscribe: null,
  };
}

async function refreshSeats(): Promise<void> {
  const o = useStore.getState().online;
  if (!o) return;
  const net = await loadNet();
  const { seats } = await net.fetchGame(o.gameId);
  const cur = useStore.getState().online;
  if (!cur || cur.gameId !== o.gameId) return;
  const mine = seats.find((s) => s.user_id === cur.userId);
  useStore.setState({ online: { ...cur, seats, myPlayerId: mine ? `p${mine.seat + 1}` : null } });
}

/**
 * Keep an attack selected between rolls while the fight can go on (a lost
 * purge with units left, or an attack the defender survived); drop it once
 * the fight is decided, the phase changes, or the move was anything else.
 */
function selectionAfter(
  sel: Selection,
  action: Action,
  before: GameState,
  after: GameState,
): Selection {
  const reset = { ...emptySelection, dice: sel.dice };
  if (after.phase !== before.phase || after.pendingOccupy) return reset;
  if (action.type === 'place') return sel;
  if (action.type !== 'attack') return reset;
  const from = after.territories[action.from]!;
  const to = after.territories[action.to]!;
  if (from.owner !== action.playerId) return reset;
  const canGoOn =
    action.from === action.to
      ? from.zombies > 0 && from.armies > 0
      : to.owner !== action.playerId && from.armies >= 2 && from.zombies < from.armies;
  return canGoOn ? sel : reset;
}

// ---------------------------------------------------------------------------
// Online moves
//
// The server is the authority, but waiting a round trip for every tap feels
// sluggish. Moves with no randomness (placing units, moving in after a
// capture, ending the attack, playing a held card) are predicted on the device
// and shown at once; dice and card draws need the server's secret seed, so
// those wait. Taps are queued and sent one at a time in order, never dropped.
// If the server refuses a move, it and anything queued after it are undone.
// ---------------------------------------------------------------------------

/** Moves the device can compute exactly from what this player may see. */
const PREDICTABLE: ReadonlySet<Action['type']> = new Set([
  'place',
  'occupy',
  'endAttack',
  'playCard',
]);

interface Queued {
  action: Action;
  /** Already shown on screen; holds the events that showed, to skip on confirmation. */
  shown: GameEvent[] | null;
}

let queue: Queued[] = [];
let inFlight = false;
/** The last state the server confirmed; the screen is this plus predicted moves. */
let confirmed: GameState | null = null;
/** A live update that arrived mid-queue, applied once the queue drains. */
let lateView: { seq: number; state: GameState; events: GameEvent[]; steps: Step[] } | null = null;

function resetQueue(): void {
  useStore.setState({ rolling: null });
  queue = [];
  inFlight = false;
  confirmed = null;
  lateView = null;
}

/** The dice a waiting attack will roll, as far as this player can see. */
function rollingFor(action: Action, game: GameState): Rolling | null {
  if (action.type !== 'attack') return null;
  const from = game.territories[action.from];
  const to = game.territories[action.to];
  if (!from || !to) return null;
  const defenders =
    action.from === action.to
      ? from.zombies
      : to.owner === 'horde' || to.owner === null
        ? to.zombies
        : to.armies;
  // An empty territory is taken without a roll.
  if (defenders <= 0) return null;
  return {
    from: action.from,
    to: action.to,
    attackDice: action.dice,
    defendDice: Math.min(2, defenders),
  };
}

function queueOnline(action: Action): boolean {
  const st = useStore.getState();
  const game = st.game!;
  // A roll or a draw is on its way, or a batch is still playing back.
  if (queue.some((q) => !q.shown) || st.replaying) return false;
  confirmed ??= game;
  if (PREDICTABLE.has(action.type)) {
    const r = reduce(worldMap, game, action);
    if (!r.ok) {
      useStore.setState({ error: r.error });
      return false;
    }
    queue.push({ action, shown: r.events });
    playBeats([
      {
        game: r.state,
        events: r.events,
        ms: 0,
        action,
        extra: { selection: selectionAfter(st.selection, action, game, r.state) },
      },
    ]);
  } else {
    queue.push({ action, shown: null });
    useStore.setState({ sending: true, error: null, rolling: rollingFor(action, game) });
    if (action.type === 'attack') play('dice');
  }
  void pump();
  return true;
}

async function pump(): Promise<void> {
  const o = useStore.getState().online;
  if (inFlight || !o || queue.length === 0) return;
  inFlight = true;
  const head = queue[0]!;
  try {
    const net = await loadNet();
    const res = await net.submitAction(o.gameId, head.action);
    if (queue[0] !== head) return; // left the game meanwhile
    queue.shift();
    onConfirmed(head, res.seq, res.state, res.events, res.steps ?? []);
  } catch (e) {
    if (queue[0] === head) undoQueue((e as Error).message);
  } finally {
    inFlight = false;
  }
  if (queue.length > 0) void pump();
  else {
    useStore.setState({ sending: false });
    const late = lateView;
    lateView = null;
    if (late) receiveView(late.seq, late.state, late.events, late.steps);
  }
}

/** The server accepted the oldest queued move. */
function onConfirmed(
  head: Queued,
  seq: number,
  state: GameState,
  events: GameEvent[],
  steps: Step[],
): void {
  const st = useStore.getState();
  const o = st.online!;
  const before = st.game!;
  confirmed = state;
  // Re-apply the moves still queued on top of the server's state.
  let shown = state;
  for (const q of queue) {
    if (!q.shown) break;
    const r = reduce(worldMap, shown, q.action);
    if (!r.ok) {
      queue = [];
      useStore.setState({ error: r.error });
      break;
    }
    shown = r.state;
  }
  useStore.setState({ online: { ...o, appliedSeq: seq } });
  // A predicted move is already on screen: play only what followed it.
  let rest = head.shown ? steps.slice(1) : steps;
  if (steps.length === 0) {
    // An older server sends no steps: one step with whatever is new.
    const prefix = head.shown ?? [];
    const same =
      prefix.length <= events.length && prefix.every((e, i) => e.type === events[i]!.type);
    const fresh = same ? events.slice(prefix.length) : events;
    rest =
      fresh.length || !head.shown
        ? [
            {
              playerId: head.action.playerId,
              actionType: head.action.type,
              events: fresh,
              board: boardOf(shown),
            },
          ]
        : [];
  }
  if (rest.length === 0) {
    if (shown !== before || st.rolling)
      playBeats([{ game: shown, events: [], ms: 0, extra: { rolling: null } }]);
    return;
  }
  const beats: StoreBeat[] = beatsForSteps(before, shown, rest, paceNow());
  const firstGame = beats[0]!.game;
  beats[0] = {
    ...beats[0]!,
    action: head.action,
    extra: {
      rolling: null,
      selection: head.shown
        ? st.selection
        : selectionAfter(st.selection, head.action, before, firstGame),
    },
  };
  playBeats(beats);
}

/** A step's board in the shape the server sends, for the no-steps fallback. */
function boardOf(g: GameState): Step['board'] {
  const territories: Step['board']['territories'] = {};
  for (const [id, t] of Object.entries(g.territories))
    territories[id] = [t.owner, t.armies, t.zombies];
  return { round: g.round, phase: g.phase, currentPlayerIndex: g.currentPlayerIndex, territories };
}

/** The server refused a move: undo it and everything queued after it. */
function undoQueue(message: string): void {
  queue = [];
  const st = useStore.getState();
  useStore.setState({
    game: confirmed ?? st.game,
    config: (confirmed ?? st.game)?.config ?? st.config,
    error: message,
    selection: { ...emptySelection, dice: st.selection.dice },
    sending: false,
    rolling: null,
  });
}

/**
 * A view from the server outside the queue: another player's move, a timeout
 * takeover, or the first view after joining. Online, the client never replays
 * moves itself (it has no seed), which is what keeps hands and dice secret.
 * Views only move forward, so late echoes are ignored.
 */
function receiveView(seq: number, game: GameState, events: GameEvent[], steps: Step[]): void {
  const o = useStore.getState().online;
  if (!o || seq <= o.appliedSeq) return;
  if (inFlight || queue.length > 0) {
    if (!lateView || seq > lateView.seq) lateView = { seq, state: game, events, steps };
    return;
  }
  confirmed = game;
  useStore.setState({ online: { ...o, appliedSeq: seq } });
  const st = useStore.getState();
  const prev = st.game ?? game;
  const batch = steps.length
    ? steps
    : [
        {
          playerId: prev.players[prev.currentPlayerIndex]?.id ?? '',
          actionType: 'endTurn' as const,
          events,
          board: boardOf(game),
        },
      ];
  const beats: StoreBeat[] = beatsForSteps(prev, game, batch, paceNow());
  beats[0] = { ...beats[0]!, extra: { selection: { ...emptySelection, dice: st.selection.dice } } };
  playBeats(beats);
}

// ---------------------------------------------------------------------------
// Playback
//
// Every change reaches the screen as beats (see lib/playback.ts): the
// player's own taps are a single beat shown at once; someone else's move,
// a rising, or a batch from the server becomes several, held long enough to
// follow. Input waits while beats are playing; Skip jumps to the end.
// ---------------------------------------------------------------------------

type StoreBeat = Beat & { extra?: Partial<Store> };

let beatQueue: StoreBeat[] = [];
let beatTimer: ReturnType<typeof setTimeout> | null = null;
let uiKey = 0;

/** Who the screen belongs to: online, this seat; locally, every human at the table. */
function isViewer(playerId: PlayerId): boolean {
  const st = useStore.getState();
  if (st.online) return playerId === st.online.myPlayerId;
  return st.game?.players.find((p) => p.id === playerId)?.kind === 'human';
}

function paceNow(): Pace {
  return { move: moveHold, fast: useStore.getState().fast, isViewer };
}

function resetPlayback(): void {
  if (beatTimer) clearTimeout(beatTimer);
  beatTimer = null;
  beatQueue = [];
  useStore.setState({
    replaying: false,
    caption: null,
    banner: null,
    floaters: [],
    fallen: null,
    lastRising: null,
  });
}

function playBeats(beats: StoreBeat[]): void {
  beatQueue.push(...beats);
  if (!beatTimer) nextBeat();
}

function nextBeat(): void {
  beatTimer = null;
  const beat = beatQueue.shift();
  if (!beat) {
    useStore.setState({ replaying: false });
    return;
  }
  presentBeat(beat);
  if (beat.ms > 0 || beatQueue.length > 0) {
    useStore.setState({ replaying: true });
    beatTimer = setTimeout(nextBeat, beat.ms);
  } else {
    useStore.setState({ replaying: false });
  }
}

/** Jump to the end of whatever is playing: the final board, every log line, no effects in between. */
function skipBeats(): void {
  if (beatTimer) clearTimeout(beatTimer);
  beatTimer = null;
  const rest = beatQueue;
  beatQueue = [];
  if (rest.length === 0) {
    useStore.setState({ replaying: false, caption: null, floaters: [] });
    return;
  }
  const st = useStore.getState();
  const last = rest.at(-1)!;
  const lines = rest.flatMap((b) => linesFor(b.events, b.game));
  const fallen =
    rest.map((b) => fallenIn(b.events, b.game, b.fallCause)).find(Boolean) ?? st.fallen;
  const extra = Object.assign({}, ...rest.map((b) => b.extra ?? {})) as Partial<Store>;
  const summary = rest
    .map((b) => b.risingSummary)
    .filter(Boolean)
    .at(-1);
  useStore.setState({
    game: last.game,
    config: last.game.config,
    lastEvents: [],
    tick: st.tick + 1,
    lines: [...st.lines, ...lines].slice(-300),
    caption: null,
    banner: null,
    floaters: [],
    fallen,
    replaying: false,
    ...(summary ? { lastRising: summary } : {}),
    ...extra,
  });
}

/** A human seat knocked out in these events, for the "you have fallen" card. */
function fallenIn(events: GameEvent[], game: GameState, cause?: string): Fallen | null {
  for (const e of events) {
    if (e.type !== 'playerEliminated') continue;
    if (!isViewer(e.playerId)) continue;
    // A finished game already says how it ended.
    if (game.outcome) continue;
    return {
      playerId: e.playerId,
      name: playerName(game, e.playerId),
      cause: cause ?? fallCause(e.playerId, game, events),
      round: game.round,
    };
  }
  return null;
}

/** Put one beat on screen: board, sounds, haptics, log lines, card reveal, captions, labels. */
function presentBeat(beat: StoreBeat): void {
  const { game, events } = beat;
  const st = useStore.getState();
  const before = st.game ?? game;
  const me = st.online?.myPlayerId ?? game.players.find((p) => p.kind === 'human')?.id ?? null;
  if (me) hapticsFor(events, me);
  const sfx = sfxFor(events, me, beat.action?.type === 'attack' && beat.action.playerId === me);
  if (sfx) play(sfx);
  if (game.outcome && !before.outcome) recordOutcome(game, me);
  let reveal: Reveal | null = null;
  const drawn = events.find((e) => e.type === 'cardDrawn' && isViewer(e.playerId));
  if (drawn && drawn.type === 'cardDrawn') {
    const effects = events
      .filter(
        (e) => e.type !== 'cardDrawn' && e.type !== 'phaseChanged' && e.type !== 'cardResolved',
      )
      .map((e) => describeEvent(e, game)?.text)
      .filter((x): x is string => !!x);
    reveal = { cardId: drawn.cardId, playerId: drawn.playerId, effects };
  }
  // A viewer's own fall is a card; anyone else's is the banner already on the beat.
  const fallen = fallenIn(events, game, beat.fallCause);
  const banner =
    beat.banner && !(fallen && beat.banner.tone === 'fallen')
      ? { ...beat.banner, key: ++uiKey }
      : beat.banner === undefined
        ? st.banner
        : null;
  const now = Date.now();
  const floaters = [
    ...st.floaters.filter((f) => now - f.at < 2500),
    ...(beat.floaters ?? []).map((f) => ({ ...f, key: ++uiKey, at: now })),
  ];
  useStore.setState({
    game,
    config: game.config,
    lastEvents: events,
    tick: st.tick + 1,
    lines: [...st.lines, ...linesFor(events, game)].slice(-300),
    reveal: reveal ?? st.reveal,
    error: null,
    caption: beat.caption !== undefined ? beat.caption : st.caption,
    banner,
    floaters,
    ...(fallen ? { fallen } : {}),
    ...(beat.risingSummary ? { lastRising: beat.risingSummary } : {}),
    ...beat.extra,
  });
}
